import { Globe } from "@phosphor-icons/react/Globe";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Bank } from "@phosphor-icons/react/Bank";
import { CaretDown } from "@phosphor-icons/react/CaretDown";
import { Leaf } from "@phosphor-icons/react/Leaf";
import { List } from "@phosphor-icons/react/List";
import { MapPinSimpleArea } from "@phosphor-icons/react/MapPinSimpleArea";
import { MapTrifold } from "@phosphor-icons/react/MapTrifold";
import { paths, routes } from "config/paths";
import RouterLink from "next/link";
import { useWebRouter } from "~utils/next-router/use-web-router";
import * as React from "react";
import { Dropdown } from "~ui-components/components/atoms/dropdown/dropdown";
import { DropdownPopover } from "~ui-components/components/atoms/dropdown/dropdown-popover";
import { DropdownTrigger } from "~ui-components/components/atoms/dropdown/dropdown-trigger";
import { MangoLogo } from "~mangomap-ui/templates/onboarding-template/fixtures/logo";
import { isNavItemActive } from "~ui-components/libs/is-nav-item-active";
import type { NavItemConfig } from "~ui-components/types/nav";
import { useAuthContext } from "~auth/contexts/auth";
import { MobileNav } from "./mobile-nav";
import Skeleton from "@mui/material/Skeleton";

// All Phosphor icons share one component type; derive it from an imported icon
// rather than barrel-importing the `Icon` type (disallowed by lint).
type PhosphorIcon = typeof Bank;

interface SolutionLinkConfig {
	href: string;
	Icon: PhosphorIcon;
	title: string;
	description: string;
}

const solutionLinks: SolutionLinkConfig[] = [
	{
		href: routes.franchise,
		Icon: MapPinSimpleArea,
		title: "Mango for Franchising",
		description: "Expert territory mapping tailored for franchise expansion.",
	},
	{
		href: paths.home,
		Icon: MapTrifold,
		title: "Mango for GIS",
		description: "Self-serve professional GIS mapping made simple.",
	},
	{
		href: paths.solutionsGovernment,
		Icon: Bank,
		title: "Mango for Government",
		description:
			"Trusted county maps — parcel, zoning, and E911 — at a fixed price.",
	},
	{
		href: paths.solutionsEnvironment,
		Icon: Leaf,
		title: "Mango for Environment",
		description:
			"No-code mapping for environmental consultants and field teams.",
	},
];

function SolutionLink({ href, Icon, title, description }: SolutionLinkConfig) {
	const router = useWebRouter();
	const active = router.pathname === href;
	return (
		<Box
			component={RouterLink}
			href={href}
			sx={{
				borderRadius: 1,
				display: "flex",
				p: 1,
				textDecoration: "none",
				"&:hover": active
					? undefined
					: {
							bgcolor: "var(--mui-palette-neutral-50)",
						},
				bgcolor: active ? "var(--mui-palette-action-selected)" : undefined,
			}}>
			<Box
				sx={{
					width: "24px",
					display: "flex",
					alignItems: "flex-start",
					justifyContent: "center",
					flexShrink: 0,
					mr: 1,
				}}>
				<Icon
					color="var(--mui-palette-primary-500)"
					size={24}
				/>
			</Box>
			<Box sx={{ flex: "1 1 auto" }}>
				<Typography
					color="text.primary"
					variant="subtitle2">
					{title}
				</Typography>
				<Typography
					color="text.secondary"
					sx={{ mt: 1 }}
					variant="body2">
					{description}
				</Typography>
			</Box>
		</Box>
	);
}

function SolutionsPopover() {
	return (
		<Box sx={{ p: 2, width: "300px" }}>
			<Stack spacing={2}>
				{solutionLinks.map((link) => (
					<SolutionLink
						key={link.title}
						{...link}
					/>
				))}
			</Stack>
		</Box>
	);
}

interface MainNavProps {
	hideAuthActions?: boolean;
}

// Now update the MainNav component to use SolutionsPopover
export function MainNav({ hideAuthActions }: MainNavProps) {
	const [openNav, setOpenNav] = React.useState<boolean>(false);
	const { pathname } = useWebRouter();
	const { isLoading, isAuthenticated, authAccount } = useAuthContext();

	return (
		<React.Fragment>
			<Box
				component="div"
				sx={{
					backdropFilter: "blur(12px)",
					color: "var(--mui-palette-common-black)",
				}}>
				<Container
					maxWidth="lg"
					sx={{
						display: "flex",
						minHeight: "var(--MainNav-height)",
						py: "16px",
					}}>
					<Stack
						direction="row"
						spacing={2}
						sx={{ alignItems: "center", flex: "1 1 auto" }}>
						<Box
							component={RouterLink}
							href={paths.home}
							sx={{ display: "inline-flex" }}>
							<MangoLogo />
						</Box>

						<Box
							component="nav"
							sx={{ display: { xs: "none", md: "block" } }}>
							<Stack
								direction="row"
								spacing={1}
								sx={{
									listStyle: "none",
									m: 0,
									p: 0,
									pl: 1,
									color: "var(--mui-palette-common-black)",
								}}>
								<NavItem
									pathname={pathname}
									title="Solutions">
									<SolutionsPopover />
								</NavItem>
								<NavItem
									external
									href="https://help.mangomap.com"
									pathname=""
									title="Documentation"
								/>
								<NavItem
									href="/pricing"
									pathname=""
									title="Pricing"
								/>
							</Stack>
						</Box>
					</Stack>
					<Stack
						direction="row"
						spacing={2}
						sx={{
							alignItems: "center",
							flex: "1 1 auto",
							justifyContent: "flex-end",
						}}>
						{hideAuthActions ? null : isLoading ? (
							<Skeleton
								variant="rectangular"
								sx={{
									display: { xs: "none", md: "flex" },
									borderRadius: "8px",
								}}
								width={120}
								height={40}
							/>
						) : isAuthenticated && authAccount ? (
							<Button
								component={RouterLink}
								href={paths.accountName.maps.root(authAccount.slug)}
								sx={{ display: { xs: "none", md: "flex" } }}
								variant="outlined"
								startIcon={<Globe />}>
								Go to portal
							</Button>
						) : (
							<React.Fragment>
								<Button
									component="a"
									href={paths.signIn}
									sx={{ display: { xs: "none", md: "flex" } }}
									variant="outlined"
									size="small">
									Sign in
								</Button>
								<Button
									component="a"
									href={paths.signUp}
									sx={{ display: { xs: "none", md: "flex" } }}
									variant="contained"
									size="small">
									Sign up
								</Button>
							</React.Fragment>
						)}
						<IconButton
							onClick={() => {
								setOpenNav(true);
							}}
							sx={{
								color: "var(--mui-palette-common-black)",
								display: { xs: "flex", md: "none" },
							}}>
							<List />
						</IconButton>
					</Stack>
				</Container>
			</Box>
			<MobileNav
				hideAuthActions={hideAuthActions}
				onClose={() => {
					setOpenNav(false);
				}}
				open={openNav}
			/>
		</React.Fragment>
	);
}

interface NavItemProps extends Omit<NavItemConfig, "key" | "items"> {
	children?: React.ReactNode;
	pathname: string;
}

export function NavItem({
	children,
	disabled,
	external,
	href,
	matcher,
	pathname,
	title,
}: NavItemProps) {
	const active = isNavItemActive({
		disabled,
		external,
		href,
		matcher,
		pathname,
	});
	const hasPopover = Boolean(children);

	const element = (
		<Box
			component="li"
			sx={{ userSelect: "none" }}>
			<Box
				{...(hasPopover
					? {
							onClick: (event: React.MouseEvent<HTMLElement>): void => {
								event.preventDefault();
							},
							role: "button",
						}
					: {
							...(href
								? {
										component: external ? "a" : RouterLink,
										href,
										target: external ? "_blank" : undefined,
										rel: external ? "noreferrer" : undefined,
									}
								: { role: "button" }),
						})}
				sx={{
					alignItems: "center",
					borderRadius: 1,
					color: "var(--mui-palette-neutral-500)",
					cursor: "pointer",
					display: "flex",
					flex: "0 0 auto",
					gap: 1,
					p: "6px 16px",
					textAlign: "left",
					textDecoration: "none",
					whiteSpace: "nowrap",
					...(disabled && {
						bgcolor: "var(--mui-palette-action-disabledBackground)",
						color: "var(--mui-action-disabled)",
						cursor: "not-allowed",
					}),
					...(active && { color: "var(--mui-palette-neutral-700)" }),
					"&:hover": {
						...(!disabled &&
							!active && {
								bgcolor: "var(--mui-palette-neutral-100)",
								color: "var(--mui-palette-neutral-700)",
							}),
					},
				}}
				tabIndex={0}>
				<Box
					component="span"
					sx={{ flex: "1 1 auto" }}>
					<Typography
						component="span"
						sx={{
							color: "inherit",
							fontSize: "0.875rem",
							fontWeight: 500,
							lineHeight: "28px",
						}}>
						{title}
					</Typography>
				</Box>
				{hasPopover ? (
					<Box
						sx={{
							alignItems: "center",
							color: "inherit",
							display: "flex",
							flex: "0 0 auto",
						}}>
						<CaretDown fontSize="var(--icon-fontSize-sm)" />
					</Box>
				) : null}
			</Box>
		</Box>
	);

	if (hasPopover) {
		return (
			<Dropdown>
				<DropdownTrigger>{element}</DropdownTrigger>
				<DropdownPopover
					PaperProps={{
						sx: {
							width: "auto",
							maxWidth: "100%",
						},
					}}
					anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
					transformOrigin={{ horizontal: "center", vertical: "top" }}>
					{children}
				</DropdownPopover>
			</Dropdown>
		);
	}

	return element;
}
